import { useCallback } from "react";
import { useCachedQuery, type UseCachedQueryResult } from "./useCachedQuery";
import { rangeToWindowHours, type InstanceRange } from "../lib/instanceDetailRange";

export type RestartEvent = {
  eventId: string; timestamp: string; instanceId: string; kind: string;
  cause: string; trigger: string; unexpected?: boolean | null; result: string | null; exitStatus: string | null;
  operationId: string | null; diagnosticCode?: string | null; incidentId?: string | null;
  instanceType?: string | null; previousInstanceType?: string | null;
  resourceSnapshot?: { GatewayMemoryPercent?: number; GatewayMemoryLimitBytes?: number; sampledAt?: string } | null;
};

export type RestartIncident = {
  incidentId: string; startedAt: string | null; recoveredAt: string | null;
  restartAttempts: number; recoverySeconds: number | null; diagnosticCode: string | null;
};

export type RestartHistory = {
  events: RestartEvent[]; restartCount: number; unexpectedRestartCount?: number; failedExitCount: number; oomEventCount: number;
  lastSeen: string | null; lastStart: string | null; coverageStart: string | null;
  incidents?: RestartIncident[];
  httpHealthy?: boolean | null; serviceActive?: boolean | null; resourceSampleAvailable?: boolean | null;
  partial: boolean; telemetryStatus: string; nextPage: number | null;
};

const UNAVAILABLE = "Restart history is temporarily unavailable.";

export const useRestartHistory = (
  tenantId: string,
  range: InstanceRange,
  page: number,
): UseCachedQueryResult<RestartHistory> => {
  const url = `/api/admin/claws/${encodeURIComponent(tenantId)}/restart-history?hours=${rangeToWindowHours(range)}&page=${page}`;
  const fetcher = useCallback(async (signal: AbortSignal): Promise<RestartHistory> => {
    const response = await fetch(url, { signal, credentials: "same-origin" });
    if (!response.ok) throw new Error(UNAVAILABLE);
    try {
      const result = await response.json() as RestartHistory;
      if (!Array.isArray(result.events)) throw new Error();
      return result;
    } catch {
      throw new Error(UNAVAILABLE);
    }
  }, [url]);
  return useCachedQuery<RestartHistory>({
    queryKey: url,
    cacheResource: range === "24h" && page === 0 ? `admin:restarts:${tenantId}` : null,
    fetcher,
  });
};
